import React, { useEffect, useState, useRef } from "react";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddWorkoutModal from "./AddWorkoutModal";
import WorkoutViewModal from "./WorkoutViewModal";
import BreathingImg from "../assets/breathing.jpg";
import FocusImg from "../assets/focus.jpg";
import HealingImg from "../assets/healing.jpg";

export default function Dashboard() {
  const [firstName, setFirstName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workouts, setWorkouts] = useState([]);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewId, setViewId] = useState(null);

  // edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);

  // delete confirmation modal state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = jwt_decode(token);
        const email = decoded.email;

        const response = await axios.get(`/api/user/${email}`);
        setFirstName(response.data.firstName);

        const workoutsRes = await axios.get("/api/workouts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWorkouts(workoutsRes.data || []);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUser();
  }, []);

  const handleAddWorkout = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const scrollLeft = () => {
    if (scrollRef.current)
      scrollRef.current.scrollBy({ left: -220, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (scrollRef.current)
      scrollRef.current.scrollBy({ left: 220, behavior: "smooth" });
  };

  // open delete confirmation
  const requestDeleteWorkout = (e, id) => {
    e.stopPropagation();
    setDeleteId(id);
    setIsDeleteConfirmOpen(true);
  };

  // confirm delete
  const confirmDeleteWorkout = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem("token");
      await axios.delete(`/api/workouts/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkouts((prev) => prev.filter((w) => w._id !== deleteId));
      if (viewId === deleteId) setIsViewOpen(false);
    } catch (err) {
      console.error("Failed to delete workout:", err);
    } finally {
      setDeleting(false);
      setIsDeleteConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const meditationTiles = [
    { key: "breathing", label: "Breathing", img: BreathingImg, path: "/meditation/breathing" },
    { key: "focus", label: "Focus", img: FocusImg, path: "/meditation/focus" },
    { key: "healing", label: "Healing", img: HealingImg, path: "/meditation/healing" },
  ];

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <div className="flex flex-grow items-center justify-center px-4">
        <div className="w-full max-w-4xl bg-white shadow-lg rounded-lg p-8">
          {/* Welcome */}
          <h1 className="text-3xl font-bold mb-4 text-left">
            Welcome, {firstName || "Loading..."}
          </h1>

          {/* Workout Section header / controls */}
          <div className="flex items-center justify-between mb-4">
            <div></div>
            <div className="flex items-center gap-2">
              <button onClick={scrollLeft} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                <ChevronLeft size={20} />
              </button>
              <button onClick={scrollRight} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                <ChevronRight size={20} />
              </button>
              <button
                type="button"
                className="text-sm text-[#e53935] hover:underline"
                onClick={() => {}}
              >
                See All
              </button>
            </div>
          </div>

          {/* Scrollable workout cards */}
          <div ref={scrollRef} className="flex space-x-4 overflow-x-auto pb-4">
            {/* Add Workout */}
            <button
              onClick={handleAddWorkout}
              className="min-w-[200px] bg-[#e53935] text-white p-4 rounded-lg shadow-md flex-shrink-0 hover:bg-black"
            >
              Add Workout
            </button>

            {/* List workouts */}
            {workouts.map((w) => (
              <div
                key={w._id}
                className="relative min-w-[200px] bg-gray-100 rounded-lg shadow-md p-4 flex-shrink-0 cursor-pointer hover:bg-gray-200 transition"
                onClick={() => { setViewId(w._id); setIsViewOpen(true); }}
              >
                {/* Delete */}
                <button
                  type="button"
                  title="Delete workout"
                  onClick={(e) => requestDeleteWorkout(e, w._id)}
                  className="absolute right-2 top-2 p-1 rounded hover:bg-red-50 text-red-600"
                >
                  <Trash2 size={16} />
                </button>

                <div className="font-semibold">
                  {new Date(w.date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Meditation Section */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Meditation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {meditationTiles.map((tile) => (
                <button
                  key={tile.key}
                  type="button"
                  className="bg-gray-100 rounded-lg shadow-md p-4 flex flex-col justify-between hover:bg-gray-200 transition"
                  onClick={() => navigate(tile.path)}
                >
                  <div className="mb-3 h-28 rounded-md overflow-hidden border border-gray-200 bg-white">
                    <img
                      src={tile.img}
                      alt={`${tile.label} meditation`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center font-semibold">{tile.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add Workout Modal (create) */}
      <AddWorkoutModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSaved={(saved) => setWorkouts((prev) => [saved, ...prev])}
      />

      {/* Edit Workout Modal */}
      <AddWorkoutModal
        mode="edit"
        initialWorkout={editingWorkout}
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingWorkout(null); }}
        onSaved={(updated) =>
          setWorkouts((prev) => prev.map((w) => (w._id === updated._id ? updated : w)))
        }
      />

      {/* View Workout Modal (edit + duplicate) */}
      <WorkoutViewModal
        workoutId={viewId}
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        onEdit={(workout) => {
          setIsViewOpen(false);
          setEditingWorkout(workout);
          setIsEditModalOpen(true);
        }}
        onDuplicate={(newWorkout) => {
          setWorkouts((prev) => [newWorkout, ...prev]);
        }}
      />

      {/* Delete Confirmation */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-2">Delete workout?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This action can’t be undone. Are you sure you want to delete this workout?
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 border border-gray-300 rounded-md py-2 hover:bg-gray-50"
                onClick={() => { setIsDeleteConfirmOpen(false); setDeleteId(null); }}
              >
                Cancel
              </button>
              <button
                className="flex-1 bg-[#e53935] text-white rounded-md py-2 hover:bg-black disabled:opacity-70"
                onClick={confirmDeleteWorkout}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}